import React, {useEffect} from 'react';
import {Box, FormControl, FormControlLabel, FormHelperText, Grid, MenuItem, Switch} from "@material-ui/core";
import {FullWidthTextField} from '@src/Shared/styles';
import {Values} from "Tools/Values";
import {State} from "@src/Interfaces/Entity/Person";
import {SelectOption} from "react-select-material-ui";
import {ItemType} from "@src/Components/Pages/Main/Content/Locations/LocationDialog/LocationDialog";
import {useFormik} from "formik";
import * as Yup from "yup";
import {IAuthStore} from "Stores/Auth/types";

const validationSchema = (uniquenessValidator: (fieldName: string) => any, primaryValidator: (id: string) => any, positionId: string) => {
    return Yup.object().shape({
        name: Yup.string().required(() => {
            return Values.getLocalizationValue("fieldRequired");
        })
            .test(
                "isNameExists",
                Values.getLocalizationValue("nameAlreadyExists"),
                uniquenessValidator("name")
            ),
        timeZone: Yup.string().required(() => {
            return Values.getLocalizationValue("fieldRequired");
        }),
        globalId: Yup.string()
            .nullable()
            .test(
                "isGlobalIdExists",
                Values.getLocalizationValue("globalIdAlreadyExists"),
                uniquenessValidator("globalId")
            ),
        imoNumber: Yup.string()
            .nullable()
            .when("type", {
                is: (type: number) => type === 1,
                then: Yup.string().test(
                    "isImoNumberExists",
                    Values.getLocalizationValue("imoNumberAlreadyExists"),
                    uniquenessValidator("imoNumber")
                ),
            }),
        mmsiNumber: Yup.string()
            .nullable()
            .when("type", {
                is: (type: number) => type === 1,
                then: Yup.string().test(
                    "isMmsiNumberExists",
                    Values.getLocalizationValue("mmsiNumberAlreadyExists"),
                    uniquenessValidator("mmsiNumber")
                )
            }),
        primary: Yup.boolean().test(
            "primaryValidation",
            Values.getLocalizationValue("selectAnotherPrimaryLocation"),
            primaryValidator(positionId)
        ),
    });
}

interface ILocationDialogDataForm {
    isNew: boolean;
    onChange?: (data: any) => void;
    setRef: (ref: any) => void;
    initialValues?: any;
    timeZones: SelectOption[];
    locationTypes: ItemType[];
    auth?: IAuthStore;
    isSysAdmin: boolean;
    uniquenessValidator: (fieldName: string) => any;
    primaryValidator: (positionId: string) => any;
    onSubmit: (data: any) => void;
    toggleFormHeight: (height: number) => void;
}

const LocationDialogDataForm = (props: ILocationDialogDataForm) => {
    const isPrimary = props.auth.user.location.primary;
    const userLocationId = props.auth.user.location.id;

    const formik = useFormik({
        initialValues: {
            name: '',
            description: '',
            globalId: '',
            routeId: '',
            imoNumber: '',
            mmsiNumber: '',
            licencePlate: '',
            id: '00000000-0000-0000-0000-000000000000',
            timeZone: '',
            type: 0,
            primary: false,
            state: State.Active,
            isVirtual: false,
        },
        onSubmit: (data) => props.onSubmit(data),
        validationSchema: validationSchema(props.uniquenessValidator, props.primaryValidator, props.initialValues?.id),
        validateOnChange: false,
        validateOnBlur: false
    });

    useEffect(() => {
        if (props.initialValues) {
            formik.setValues({...props.initialValues});
        }
    }, []);
    
    useEffect(() => {
        props.onChange(formik.values);
    }, [formik.values]);

    useEffect(() => {
        props.toggleFormHeight(document.getElementById("locationDialog").clientHeight);
    }, [formik.values.type]);
    
    const isStateSwitchDisabled = () => {
        if (isPrimary) {
            return userLocationId === props.initialValues?.id;
        } else return true;
    }

    const isPrimarySwitchDisabled = () => {
        if (isPrimary) {
            if (props.isSysAdmin) {
                return false;
            }
            return props.initialValues?.state === 0
        } else return true
    }
    
    const isRoteIdDisabled = () => {
        return !(userLocationId === props.initialValues?.id || isPrimary);
    }
    
    const isTypeChangeDisabled = () => {
        if (props.isNew || isPrimary){
            return userLocationId === props.initialValues?.id;
        }
        return true;
    }

    const onStateChange = (checked: boolean) => {
        const state = checked ? State.Active : State.Deactivated;
        formik.setFieldValue("state", state);
    }

    const renderRouteIdField = () => (
        formik.values.type != 0 &&
        <Grid item>
            <FullWidthTextField
                data-test-id={"routeId-field"}
                name={"routeId"}
                onChange={formik.handleChange}
                value={formik.values.routeId}
                size={"small"}
                label={Values.getLocalizationValue("routeId")}
                variant="outlined"
                disabled={isRoteIdDisabled()}
            />
        </Grid>
    );

    const renderImoAndMmsiNumberFields = () => (
        formik.values.type === 1 &&
        <>
            <Grid item>
                <FullWidthTextField
                    data-test-id={"imoNumber-field"}
                    name={"imoNumber"}
                    onChange={formik.handleChange}
                    value={formik.values.imoNumber}
                    size={"small"}
                    error={formik.errors.imoNumber}
                    helperText={formik.errors.imoNumber}
                    label={Values.getLocalizationValue("iMONumber")}
                    variant="outlined"
                    disabled={!isPrimary}
                />
            </Grid>

            <Grid item>
                <FullWidthTextField
                    data-test-id={"mmsiNumber-field"}
                    name={"mmsiNumber"}
                    onChange={formik.handleChange}
                    value={formik.values.mmsiNumber}
                    size={"small"}
                    error={formik.errors.mmsiNumber}
                    helperText={formik.errors.mmsiNumber}
                    label={Values.getLocalizationValue("mMSINumber")}
                    variant="outlined"
                    disabled={!isPrimary}
                />
            </Grid>
        </>
    );

    const renderLicencePlateFiled = () => (
        formik.values.type === 2 &&
        <Grid item>
            <FullWidthTextField
                data-test-id={"licencePlate-field"}
                name={"licencePlate"}
                onChange={formik.handleChange}
                value={formik.values.licencePlate}
                size={"small"}
                error={formik.errors.licencePlate}
                helperText={formik.errors.licencePlate}
                label={Values.getLocalizationValue("licencePlate")}
                variant="outlined"
                disabled={!isPrimary}
            />
        </Grid>
    );

    const renderLocationId = () => (
        !props.isNew &&
        <Grid item>
            <FullWidthTextField
                data-test-id={"locationId-field"}
                name={"id"}
                value={formik.values.id}
                size={"small"}
                label={Values.getLocalizationValue("locationId")}
                variant="outlined"
                disabled={true}
            />
        </Grid>
    )

    return (
        <form onSubmit={formik.handleSubmit} ref={ref => props.setRef(ref)}>
            <Grid container spacing={2} direction={"column"}>
                <Grid item>
                    <FullWidthTextField
                        data-test-id={"name-field"}
                        name={"name"}
                        onChange={formik.handleChange}
                        value={formik.values.name}
                        size={"small"}
                        error={formik.errors.name}
                        helperText={formik.errors.name}
                        label={Values.getLocalizationValue("name")}
                        variant="outlined"
                        disabled={!isPrimary}
                    />
                </Grid>

                <Grid item>
                    <FullWidthTextField
                        data-test-id={"description-field"}
                        name={"description"}
                        onChange={formik.handleChange}
                        value={formik.values.description}
                        size={"small"}
                        error={formik.errors.description}
                        helperText={formik.errors.description}
                        label={Values.getLocalizationValue("description")}
                        variant="outlined"
                        disabled={!isPrimary}
                    />
                </Grid>

                {renderLicencePlateFiled()}
                {renderImoAndMmsiNumberFields()}

                <Grid item>
                    <FullWidthTextField
                        data-test-id={"globalId-field"}
                        name={"globalId"}
                        onChange={formik.handleChange}
                        value={formik.values.globalId}
                        size={"small"}
                        error={formik.errors.globalId}
                        helperText={formik.errors.globalId}
                        label={Values.getLocalizationValue("globalId")}
                        variant="outlined"
                        disabled={!isPrimary}
                    />
                </Grid>

                {renderRouteIdField()}
                {renderLocationId()}

                <Grid item>
                    <FullWidthTextField
                        select
                        data-test-id={"timeZone-field"}
                        name={"timeZone"}
                        onChange={formik.handleChange}
                        value={formik.values.timeZone}
                        size={"small"}
                        error={formik.errors.timeZone}
                        helperText={formik.errors.timeZone}
                        label={Values.getLocalizationValue("timeZone")}
                        variant="outlined"
                        disabled={!isPrimary}
                    >
                        {props.timeZones?.map((item) => (
                            <MenuItem key={item.value} value={item.value} data-test-id={`menu-item-${item.value}`}>
                                {item.label}
                            </MenuItem>
                        ))}
                    </FullWidthTextField>
                </Grid>

                <Grid item>
                    <FullWidthTextField
                        select
                        data-test-id={"type-field"}
                        name={"type"}
                        onChange={formik.handleChange}
                        value={formik.values.type}
                        size={"small"}
                        label={Values.getLocalizationValue("type")}
                        variant="outlined"
                        disabled={isTypeChangeDisabled()}
                    >
                        {props.locationTypes?.map((item) => (
                            <MenuItem key={item.type} value={item.type} data-test-id={`menu-item-${item.type}`}>
                                {item.title}
                            </MenuItem>
                        ))}
                    </FullWidthTextField>
                </Grid>

                <Grid item>
                    <Box pl={1} pt={1}>
                        <FormControl error={!!formik.errors?.primary}>
                            <FormControlLabel
                                label={Values.getLocalizationValue("primary")}
                                control={
                                    <Switch
                                        data-test-id={"primary-field"}
                                        color={"primary"}
                                        name={"primary"}
                                        onChange={formik.handleChange}
                                        checked={formik.values.primary}
                                        disabled={isPrimarySwitchDisabled()}
                                    />
                                }
                            />
                            <FormHelperText>{formik.errors?.primary}</FormHelperText>
                        </FormControl>
                    </Box>
                </Grid>

                <Grid item>
                    <Box pl={1} pt={1}>
                        <FormControlLabel
                            label={Values.getLocalizationValue("active")}
                            control={
                                <Switch
                                    data-test-id={"state-field"}
                                    color={"primary"}
                                    name={"state"}
                                    onChange={(ev) => onStateChange(ev.target.checked)}
                                    checked={formik.values.state === State.Active}
                                    disabled={isStateSwitchDisabled()}
                                />
                            }
                        />
                    </Box>
                </Grid>

                <Grid item>
                    <Box pl={1} pt={1}>
                        <FormControlLabel
                            label={Values.getLocalizationValue("virtual")}
                            control={
                                <Switch
                                    data-test-id={"isVirtual-field"}
                                    color={"primary"}
                                    name={"isVirtual"}
                                    onChange={formik.handleChange}
                                    checked={formik.values.isVirtual}
                                    disabled={!(isPrimary && props.isSysAdmin)}
                                />
                            }
                        />
                    </Box>
                </Grid>
            </Grid>
        </form>
    );
};

export default LocationDialogDataForm;
